import {
  storyCardsSchema,
  type Localized,
  type StoryCard,
  type WomanJobStatus,
  type WomanStoryStatus,
} from '@org/model';
import type { StoryWoman } from '@org/ui';

// Pure helpers of the woman editor (no React, no tRPC) — kept here so the
// logic can be read and tested on its own.

/** Text fields of the profile, edited as plain strings ('' = empty). */
export const WOMAN_TEXT_FIELDS = [
  'name',
  'years',
  'field',
  'fieldPt',
  'nationality',
  'nationalityPt',
  'symbol',
  'portraitUrl',
  'referencePhotoUrl',
  'quote',
  'quotePt',
  'quoteSource',
  'characteristic',
  'characteristicEn',
] as const;

export type WomanTextField = (typeof WOMAN_TEXT_FIELDS)[number];

/** What the editor holds while the admin types (the unsaved state). */
export type WomanFormState = Record<WomanTextField, string> & {
  taglineEn: string;
  taglinePt: string;
  storyCards: StoryCard[];
  storyStatus: WomanStoryStatus;
};

/** The slice of a `women` row the editor reads. */
export type WomanRowLike = {
  [K in WomanTextField]?: string | null;
} & {
  tagline?: Localized | null;
  storyCards?: StoryCard[] | null;
  storyStatus?: WomanStoryStatus | null;
};

const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `card-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Every card needs a unique, non-empty id (drag & drop and React keys rely on
 * it). Cards pasted as JSON or drafted by the AI may miss one or repeat one.
 */
export function ensureCardIds<T>(cards: readonly T[]): T[] {
  const seen = new Set<string>();
  return cards.map((card) => {
    if (!card || typeof card !== 'object' || Array.isArray(card)) return card;
    const current = (card as { id?: unknown }).id;
    let id = typeof current === 'string' ? current.trim() : '';
    if (!id || seen.has(id)) id = newId();
    seen.add(id);
    return id === current ? card : { ...card, id };
  });
}

/** A copy of a card with a fresh id. */
export function duplicateCard(card: StoryCard): StoryCard {
  return { ...(JSON.parse(JSON.stringify(card)) as StoryCard), id: newId() };
}

export function formFromWoman(row: WomanRowLike): WomanFormState {
  const form = {} as WomanFormState;
  for (const key of WOMAN_TEXT_FIELDS) form[key] = row[key] ?? '';
  form.taglineEn = row.tagline?.en ?? '';
  form.taglinePt = row.tagline?.pt ?? '';
  form.storyCards = ensureCardIds(Array.isArray(row.storyCards) ? row.storyCards : []);
  form.storyStatus = row.storyStatus === 'published' ? 'published' : 'draft';
  return form;
}

export function isFormDirty(form: WomanFormState, saved: WomanFormState): boolean {
  return JSON.stringify(form) !== JSON.stringify(saved);
}

export type WomanUpdatePatch = Partial<Record<WomanTextField, string>> & {
  tagline?: Localized | null;
  storyCards?: StoryCard[];
  storyStatus?: WomanStoryStatus;
};

/**
 * Only what changed since the last save. Sending a partial update means a
 * field the admin did not touch is never overwritten (for example the portrait
 * the AI finished while she was typing).
 */
export function buildUpdatePatch(form: WomanFormState, saved: WomanFormState): WomanUpdatePatch {
  const patch: WomanUpdatePatch = {};
  for (const key of WOMAN_TEXT_FIELDS) {
    if (form[key] !== saved[key]) patch[key] = key === 'name' ? form[key].trim() : form[key];
  }
  if (form.taglineEn !== saved.taglineEn || form.taglinePt !== saved.taglinePt) {
    const en = form.taglineEn.trim();
    const pt = form.taglinePt.trim();
    patch.tagline = en || pt ? { en, pt } : null;
  }
  if (JSON.stringify(form.storyCards) !== JSON.stringify(saved.storyCards)) {
    patch.storyCards = form.storyCards;
  }
  if (form.storyStatus !== saved.storyStatus) patch.storyStatus = form.storyStatus;
  return patch;
}

export type PublishBlocker = 'portrait' | 'tagline';

/** Why the story cannot be published yet (empty = it can). */
export function publishBlockers(form: Pick<WomanFormState, 'portraitUrl' | 'taglineEn' | 'taglinePt'>): PublishBlocker[] {
  const blockers: PublishBlocker[] = [];
  if (!form.portraitUrl.trim()) blockers.push('portrait');
  if (!form.taglineEn.trim() && !form.taglinePt.trim()) blockers.push('tagline');
  return blockers;
}

export function isJobRunning(status: WomanJobStatus | null | undefined): boolean {
  return status === 'pending' || status === 'generating';
}

/** The AI worker needs a public web address for the reference photo. */
export function isHttpUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** The woman as the story viewer wants her, from the unsaved form. */
export function storyWomanFromForm(form: WomanFormState, id?: string): StoryWoman {
  const orNull = (value: string) => (value.trim() ? value : null);
  return {
    id,
    name: form.name.trim() || '…',
    years: orNull(form.years),
    field: orNull(form.field),
    fieldPt: orNull(form.fieldPt),
    nationality: orNull(form.nationality),
    nationalityPt: orNull(form.nationalityPt),
    symbol: orNull(form.symbol),
    portraitUrl: orNull(form.portraitUrl),
    quote: orNull(form.quote),
    quotePt: orNull(form.quotePt),
    quoteSource: orNull(form.quoteSource),
    characteristic: orNull(form.characteristic),
    characteristicEn: orNull(form.characteristicEn),
    tagline: { en: form.taglineEn, pt: form.taglinePt },
  };
}

const firstText = (...values: (string | null | undefined)[]): string =>
  values.map((v) => (typeof v === 'string' ? v.trim() : '')).find(Boolean) ?? '';

/** The short second line of a card in the list: its heading or first words. */
export function cardSummary(card: StoryCard, lang: 'en' | 'pt', maxLength = 80): string {
  const other = lang === 'en' ? 'pt' : 'en';
  const loc = (value: Localized | undefined) => firstText(value?.[lang], value?.[other]);
  let text = loc(card.title);
  if (!text) {
    switch (card.type) {
      case 'text':
        text = firstText(loc(card.body), card.big);
        break;
      case 'video':
        text = firstText(loc(card.caption), card.url);
        break;
      case 'notebook':
        text = firstText(card.original, loc(card.gloss));
        break;
      case 'quote':
        text = loc(card.body);
        break;
    }
  }
  const clean = text.replace(/\*(.+?)\*/g, '$1').replace(/\s+/g, ' ').trim();
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1).trimEnd()}…` : clean;
}

export type CardsJsonIssue = {
  /** 1-based card number, or null when the problem is about the whole list */
  card: number | null;
  /** where in the card, e.g. "body.en" ('' = the card itself) */
  field: string;
  message: string;
  /** the list holds more cards than allowed */
  tooMany?: boolean;
};

export type CardsJsonResult =
  | { ok: true; cards: StoryCard[] }
  | { ok: false; reason: 'syntax'; message: string }
  | { ok: false; reason: 'not-a-list' }
  | { ok: false; reason: 'invalid'; issues: CardsJsonIssue[] };

/** Reads the JSON tab: parses, fills in missing ids, validates with the shared schema. */
export function parseCardsJson(text: string): CardsJsonResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text.trim() === '' ? '[]' : text);
  } catch (error) {
    return { ok: false, reason: 'syntax', message: error instanceof Error ? error.message : String(error) };
  }
  if (!Array.isArray(raw)) return { ok: false, reason: 'not-a-list' };

  const parsed = storyCardsSchema.safeParse(ensureCardIds(raw));
  if (parsed.success) return { ok: true, cards: parsed.data };

  const issues = parsed.error.issues.map((issue): CardsJsonIssue => {
    const [first, ...rest] = issue.path;
    const card = typeof first === 'number' ? first + 1 : null;
    const fieldPath = card === null ? issue.path : rest;
    return {
      card,
      field: fieldPath.map(String).join('.'),
      message: issue.message,
      tooMany: issue.path.length === 0 && issue.code === 'too_big',
    };
  });
  return { ok: false, reason: 'invalid', issues };
}

export function cardsToJson(cards: readonly StoryCard[]): string {
  return JSON.stringify(cards, null, 2);
}

// Errors of the "create → ask the AI" step, handed from the "new woman" modal
// to the editor page it navigates to (same browser session only).
export type WomanRequestErrors = { portrait?: string; draft?: string };
const handoff = new Map<string, WomanRequestErrors>();

export function stashRequestErrors(womanId: string, errors: WomanRequestErrors): void {
  if (errors.portrait || errors.draft) handoff.set(womanId, errors);
}

export function peekRequestErrors(womanId: string): WomanRequestErrors {
  return handoff.get(womanId) ?? {};
}

export function clearRequestErrors(womanId: string): void {
  handoff.delete(womanId);
}
